import druvaSvgUrl from "assets/images/druva-logo-new.svg";

/** Base32 secret grouped as 4-character blocks for PDF (matches Figma reference). */
export function formatSoftwareTokenForPdf(token) {
  const cleaned = String(token || "")
    .replace(/\s/g, "")
    .toUpperCase();
  const groups = cleaned.match(/.{1,4}/g);
  return groups ? groups.join(" ") : cleaned;
}

const asTextLines = (chunk) => (Array.isArray(chunk) ? chunk : [String(chunk)]);

/**
 * Rasterizes bundled SVG to PNG for jsPDF (jspdf does not render SVG directly).
 * Uses fetch + canvas; if this fails in an environment, the PDF is generated without the logo.
 *
 * @param {string} svgUrl
 * @returns {Promise<string | null>}
 */
async function loadSvgAssetAsPngDataUrl(svgUrl) {
  try {
    const response = await fetch(svgUrl);
    if (!response.ok) return null;
    const svgText = await response.text();
    const blob = new Blob([svgText], {
      type: "image/svg+xml;charset=utf-8",
    });
    const objectUrl = URL.createObjectURL(blob);
    try {
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("SVG rasterize failed"));
        img.src = objectUrl;
      });
      const scale = 4;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(77 * scale);
      canvas.height = Math.round(28 * scale);
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL("image/png");
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch {
    return null;
  }
}

/**
 * Builds and saves the TOTP authenticator setup PDF (Figma-aligned layout).
 *
 * @param {{ qrCodeDataUrl: string; softwareToken: string }} params
 * @returns {Promise<void>}
 */
export async function downloadMfaTotpConfigurationPdf({
  qrCodeDataUrl,
  softwareToken,
}) {
  const { default: JsPDF } = await import("jspdf");
  const doc = new JsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  const marginXMm = 14;
  /** jsPDF `text` uses baseline; `addImage` uses top-left — offset so heading cap aligns with QR top. */
  const baselineOffsetFromCellTopMm = (fontSizePt) =>
    fontSizePt * 0.352778 * 0.72;

  let y = 14;

  const logoPng = await loadSvgAssetAsPngDataUrl(druvaSvgUrl);
  const logoWmm = 32;
  const logoHmm = (28 / 77) * logoWmm;
  if (logoPng) {
    doc.addImage(logoPng, "PNG", marginXMm, y, logoWmm, logoHmm);
    y += logoHmm;
  }
  y += 8;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  const title = "Authenticator App Configuration Code";
  doc.text(title, pageW / 2, y, { align: "center" });
  y += doc.getTextDimensions(title, { fontSize: 18 }).h + 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  const intro =
    "Scan below QR code or manually enter code below to re-configure a different authenticator app for multifactor authentication.";
  const introMaxW = pageW - 2 * marginXMm;
  const introLines = asTextLines(doc.splitTextToSize(intro, introMaxW));
  const introLineMm = 6.5;
  introLines.forEach((line) => {
    doc.text(line, marginXMm, y);
    y += introLineMm;
  });
  y += 9;

  const qrSizeMm = 58;
  const qrXMm = marginXMm;
  const qrYMm = y;
  const qrBottomMm = qrYMm + qrSizeMm;
  doc.addImage(qrCodeDataUrl, "PNG", qrXMm, qrYMm, qrSizeMm, qrSizeMm);

  const colGapMm = 10;
  const colXMm = qrXMm + qrSizeMm + colGapMm;
  const colW = pageW - colXMm - marginXMm;
  const lineHeight12 = 6.2;
  const lineHeight14 = 7.2;
  const minGapHeadingToStepsMm = 2;
  const minGapStepsToSecretMm = 3;

  const stepsHeading = "Follow below steps to configure authenticator app";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  const headingLines = asTextLines(doc.splitTextToSize(stepsHeading, colW));

  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  const stepParagraphs = [
    "1. Download the 'Authenticator app'.",
    "2. In app, select 'set up an account'.",
    "3. Scan the given QR code or manually enter below code in the app, to obtain 6 digit OTP.",
  ];
  const stepLineGroups = stepParagraphs.map((p) =>
    asTextLines(doc.splitTextToSize(p, colW))
  );
  const stepInterBlockMm = 1.2;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  const formattedSecret = formatSoftwareTokenForPdf(softwareToken);
  const secretLines = asTextLines(doc.splitTextToSize(formattedSecret, colW));

  const firstHeadingBaseline = qrYMm + baselineOffsetFromCellTopMm(12);

  let ySteps = firstHeadingBaseline;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  headingLines.forEach((line) => {
    doc.text(line, colXMm, ySteps);
    ySteps += lineHeight12;
  });
  ySteps += minGapHeadingToStepsMm;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  stepLineGroups.forEach((lines, idx) => {
    lines.forEach((line) => {
      doc.text(line, colXMm, ySteps);
      ySteps += lineHeight12;
    });
    if (idx < stepLineGroups.length - 1) {
      ySteps += stepInterBlockMm;
    }
  });

  const yAfterSteps = ySteps;
  const qrBottomPadMm = 1.5;
  const targetLastSecretBaseline = qrBottomMm - qrBottomPadMm;
  const nSecret = secretLines.length;
  const firstSecretBaseline =
    nSecret > 0
      ? Math.max(
          yAfterSteps + minGapStepsToSecretMm,
          targetLastSecretBaseline - (nSecret - 1) * lineHeight14
        )
      : yAfterSteps;
  ySteps = firstSecretBaseline;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  secretLines.forEach((line) => {
    doc.text(line, colXMm, ySteps);
    ySteps += lineHeight14;
  });

  if (process.env.NODE_ENV !== "test") {
    doc.save("Druva-MFA-QRcode.pdf");
  }
}
